// react
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui
import { Dialog, DialogContent, DialogTitle } from '@mui/material';

// api
import type { GetEnergyTreeNodeDto } from '@/api/generated/recalc/endpoints/v2/energyGroups';
import {
  useGetEnergyGroupQuery,
  usePutEnergyGroupEnergyTreeNodeMutation,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
// shared-components
import { WritableOnly } from '@/components/access-control';

// feature-utils
import { getInitiativeInclusionsForNode } from '@/features/energyxplorer/utils/energyTreeNodeInitiativeInclusion';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import EditNodeForm from '../../forms/EditNodeForm';
import { toPutEnergyTreeNodeDto } from '../../forms/editNodeForm.mappers';
import type { EditNodeFormValues } from '../../forms/editNodeForm.schema';
import { isResidualNode } from '../../utils/energyTreeFunctions';

interface Unit {
  UnitId: number;
  Name: string;
  Tags?: Array<{ TagId: number; Text: string }>;
  UnitSensors?: Array<{ Sensor?: { SensorId: number } }>;
  usedInNode?: string;
  notSupportedReason?: string;
}

type EditNodeDialogProps = {
  open: boolean;
  onClose: () => void;
  energyTreeNode?: GetEnergyTreeNodeDto;
  units: Unit[];
  mode?: 'server' | 'local';
  onConfirm?: (node: GetEnergyTreeNodeDto) => void;
};

const EditNodeDialog = ({
  open,
  onClose,
  energyTreeNode,
  units,
  mode = 'server',
  onConfirm,
}: EditNodeDialogProps) => {
  const { t } = useTranslation(['energyxplorer-tree-tab', 'validation']);
  const dispatch = useDispatch();

  const { energyGroupId } = useParams({ strict: false }) as {
    energyGroupId?: string;
  };

  const parsedEnergyGroupId = energyGroupId
    ? parseInt(energyGroupId, 10)
    : undefined;

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: parsedEnergyGroupId! },
    {
      skip: !parsedEnergyGroupId,
    },
  );

  const [putEnergyTreeNode, { isLoading: isUpdating }] =
    usePutEnergyGroupEnergyTreeNodeMutation();

  const inclusions = useMemo(
    () =>
      getInitiativeInclusionsForNode(
        energyTreeNode?.energyTreeNodeId ?? '',
        selectedEnergyGroup ?? {},
      ),
    [energyTreeNode?.energyTreeNodeId, selectedEnergyGroup],
  );

  const virtualInitiativeInclusions = useMemo(
    () =>
      (inclusions || []).filter(({ viaNode }) => {
        if (!viaNode || typeof viaNode !== 'object') return false;
        const node = viaNode as { dataStreams?: unknown[] };
        return !node.dataStreams || node.dataStreams.length === 0;
      }),
    [inclusions],
  );

  const handleSubmit = async (values: EditNodeFormValues) => {
    if (!energyTreeNode) return;

    const dto = toPutEnergyTreeNodeDto(values);

    // Build the updated node payload
    const updatedNode = {
      ...energyTreeNode,
      ...dto,
      // If residual, ensure no dataStreams
      dataStreams: isResidualNode(energyTreeNode) ? [] : dto.dataStreams,
    } as GetEnergyTreeNodeDto;

    // ✅ LOCAL MODE
    if (mode === 'local') {
      if (typeof onConfirm !== 'function') {
        dispatch(
          showSnackbar({
            message: t('shared_dialog.local_mode_handler_missing', {
              ns: 'energyxplorer-tree-tab',
            }),
            severity: 'error',
          }),
        );
        return;
      }

      onConfirm(updatedNode);
      dispatch(
        showSnackbar({
          message: t('edit_node_dialog.update_success_draft', {
            ns: 'energyxplorer-tree-tab',
            defaultValue: 'Node opdateret (kladde)',
          }),
          severity: 'success',
        }),
      );
      onClose();
      return;
    }

    // ✅ SERVER MODE
    if (!parsedEnergyGroupId) return;

    const result = await putEnergyTreeNode({
      groupId: parsedEnergyGroupId,
      nodeId: energyTreeNode.energyTreeNodeId ?? '',
      putEnergyTreeNodeDto: dto,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('edit_node_dialog.update_success', {
            ns: 'energyxplorer-tree-tab',
            defaultValue: 'Node opdateret',
          }),
          severity: 'success',
        }),
      );
      onClose();
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  if (!energyTreeNode) return null;

  return (
    <Dialog
      data-testid="orgchart-edit-node-dialog"
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {t('nodes.edit_dialog.edit_node_title', { ns: 'energyxplorer' })}
      </DialogTitle>
      <WritableOnly>
        <DialogContent>
          <EditNodeForm
            energyTreeNode={energyTreeNode}
            units={units}
            virtualInitiativeInclusions={virtualInitiativeInclusions}
            onSubmit={handleSubmit}
            onCancel={onClose}
            isSubmitting={isUpdating}
          />
        </DialogContent>
      </WritableOnly>
    </Dialog>
  );
};

export default EditNodeDialog;
